<template>
  <div>
    <PageHeader
      eyebrow="Hotel Booking"
      title="Hotels in Makkah &amp; Madina"
      subtitle="Book your stay near the Haram with transparent per-night pricing. Filter by city to compare distance, rating, and rates."
    />

    <section class="container mx-auto px-4 py-16">
      <!-- City Filter -->
      <div class="flex flex-wrap items-center justify-center gap-2 mb-4">
        <button
          v-for="city in cities"
          :key="city"
          @click="selectedCity = city"
          :class="[
            'rounded-full px-5 py-2 text-sm font-semibold transition',
            selectedCity === city ? 'bg-amber-500 text-white shadow-md shadow-amber-500/30' : 'bg-white border border-gray-200 text-gray-600 hover:border-amber-300 hover:text-amber-600'
          ]"
        >
          {{ city }}
        </button>
      </div>

      <!-- Star Rating Filter -->
      <div class="flex flex-wrap items-center justify-center gap-2 mb-12">
        <button
          v-for="rating in ratings"
          :key="rating"
          @click="selectedRating = rating"
          :class="[
            'rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-wide transition',
            selectedRating === rating ? 'bg-gray-900 text-white' : 'bg-white border border-gray-200 text-gray-500 hover:border-gray-400 hover:text-gray-700'
          ]"
        >
          {{ rating === 'All' ? 'All Ratings' : `${rating} Star` }}
        </button>
      </div>

      <!-- Hotel Cards -->
      <div class="grid gap-8 md:grid-cols-2 xl:grid-cols-3">
        <article
          v-for="hotel in filteredHotels"
          :key="hotel.name"
          class="overflow-hidden rounded-[28px] border border-gray-100 bg-white shadow-lg transition hover:-translate-y-1 hover:shadow-xl"
        >
          <div class="relative h-52 overflow-hidden">
            <img :src="hotel.image" :alt="hotel.name" class="h-full w-full object-cover transition duration-500 hover:scale-105" />
            <div class="absolute top-4 left-4 rounded-full bg-white/90 backdrop-blur-sm px-3 py-1 text-xs font-semibold uppercase tracking-wide text-gray-700">
              {{ hotel.city }}
            </div>
          </div>
          <div class="p-6">
            <div class="flex items-center gap-1 mb-2">
              <svg v-for="i in 5" :key="i" class="h-4 w-4" :class="i <= hotel.rating ? 'text-amber-400' : 'text-gray-200'" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.97a1 1 0 00.95.69h4.178c.969 0 1.371 1.24.588 1.81l-3.384 2.459a1 1 0 00-.364 1.118l1.286 3.97c.3.921-.755 1.688-1.54 1.118l-3.384-2.46a1 1 0 00-1.176 0l-3.384 2.46c-.785.57-1.84-.197-1.54-1.118l1.286-3.97a1 1 0 00-.364-1.118L2.045 9.397c-.783-.57-.38-1.81.588-1.81h4.178a1 1 0 00.95-.69l1.286-3.97z" />
              </svg>
            </div>
            <h3 class="text-xl font-bold text-gray-900">{{ hotel.name }}</h3>
            <p class="mt-1 flex items-center gap-1.5 text-sm text-gray-500">
              <svg class="h-4 w-4 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
              </svg>
              {{ hotel.distance }}
            </p>
            <ul class="mt-3 space-y-1">
              <li v-for="(amenity, idx) in hotel.amenities" :key="idx" class="flex items-center gap-2 text-sm text-gray-600">
                <svg class="h-3.5 w-3.5 flex-shrink-0 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                </svg>
                {{ amenity }}
              </li>
            </ul>

            <div class="mt-5 flex items-end justify-between border-t border-gray-100 pt-4">
              <div>
                <p class="text-xs uppercase tracking-wide text-gray-400">Per Night</p>
                <p class="text-xl font-bold text-gray-900">{{ formatPrice(hotel.pricePerNight) }}</p>
              </div>
              <a
                :href="whatsappUrl(hotel)"
                target="_blank"
                rel="noopener noreferrer"
                class="rounded-full bg-amber-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-amber-600"
              >
                Book Now
              </a>
            </div>
          </div>
        </article>
      </div>
    </section>
  </div>
</template>

<script>
import PageHeader from '../components/PageHeader.vue'
import { formatPrice } from '../services/currencyStore'
import { listItems } from '../services/dataStore'

// Makkah — Kaaba / Clock Tower (used for Makkah-only listings)
const MAKKAH_LANDMARK = 'https://images.unsplash.com/photo-1580418827493-f2b22c0a76cb?w=800&q=80'
// Madina — Masjid Nabawi at golden hour (used for Madina-only listings)
const MADINA_LANDMARK = 'https://images.unsplash.com/photo-1591604129939-f1efa4d9f7fa?w=800&q=80'
// Generic hotel interiors, safe for either city
const ROOM1 = 'https://images.unsplash.com/photo-1611892440504-42a792e24d32?w=800&q=80'
const ROOM2 = 'https://images.unsplash.com/photo-1445019980597-93fa8acb246c?w=800&q=80'
const ROOM3 = 'https://images.unsplash.com/photo-1590490360182-c33d57733427?w=800&q=80'
const ROOM4 = 'https://images.unsplash.com/photo-1618773928121-c32242e63f39?w=800&q=80'
const ROOM5 = 'https://images.unsplash.com/photo-1611048267451-e6ed903d4a38?w=800&q=80'
const ROOM6 = 'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=800&q=80'
const ROOM7 = 'https://images.unsplash.com/photo-1590490359683-658d3d23f972?w=800&q=80'

export default {
  name: 'HotelsPage',
  components: {
    PageHeader
  },
  data() {
    return {
      whatsappNumber: '923185073318',
      selectedCity: 'All',
      selectedRating: 'All',
      cities: ['All', 'Makkah', 'Madina'],
      ratings: ['All', 5, 4, 3],
      hotels: [
        // Makkah — 5 Star
        { name: 'Fairmont Makkah Clock Royal Tower', city: 'Makkah', distance: 'Attached to Masjid al-Haram', rating: 5, pricePerNight: 85000, amenities: ['Direct Kaaba view rooms', 'Clock Tower Haram access', 'Rooftop fine dining'], image: MAKKAH_LANDMARK },
        { name: 'Raffles Makkah Palace', city: 'Makkah', distance: '100m from Haram', rating: 5, pricePerNight: 98000, amenities: ['Private butler service', 'Kaaba view suites', 'Limousine airport transfer'], image: ROOM1 },
        { name: 'Conrad Makkah', city: 'Makkah', distance: '300m from Haram', rating: 5, pricePerNight: 72000, amenities: ['Executive lounge access', 'Infinity pool with city view', 'Multiple fine dining restaurants'], image: ROOM2 },
        { name: 'Swissôtel Al Maqam', city: 'Makkah', distance: '150m from Haram', rating: 5, pricePerNight: 45000, amenities: ['Kaaba view rooms available', 'Direct Haram access', 'Breakfast included'], image: ROOM3 },
        { name: 'Jabal Omar Hyatt Regency', city: 'Makkah', distance: '400m from Haram', rating: 5, pricePerNight: 38000, amenities: ['Shuttle to Haram', 'Executive lounge', 'Family rooms'], image: ROOM4 },
        { name: 'Pullman Zamzam Makkah', city: 'Makkah', distance: '250m from Haram', rating: 5, pricePerNight: 42000, amenities: ['Rooftop Kaaba view restaurant', 'Executive rooms', 'Free WiFi'], image: ROOM5 },

        // Makkah — 4 Star
        { name: 'Hilton Convention Makkah', city: 'Makkah', distance: '600m from Haram', rating: 4, pricePerNight: 26000, amenities: ['Free WiFi', 'Restaurant on-site', 'Airport transfer available'], image: ROOM6 },
        { name: 'Le Meridien Towers Makkah', city: 'Makkah', distance: '500m from Haram', rating: 4, pricePerNight: 30000, amenities: ['Direct mall access', 'Family rooms', 'Shuttle to Haram'], image: ROOM7 },
        { name: 'Elaf Ajyad Hotel', city: 'Makkah', distance: '700m from Haram', rating: 4, pricePerNight: 24000, amenities: ['Free WiFi', 'Restaurant on-site', '24-hour front desk'], image: ROOM1 },
        { name: 'Voco Makkah', city: 'Makkah', distance: '800m from Haram', rating: 4, pricePerNight: 22000, amenities: ['Modern rooms', 'Breakfast included', 'Airport transfer available'], image: ROOM2 },

        // Makkah — 3 Star
        { name: 'Al Kiswah Towers', city: 'Makkah', distance: '900m from Haram', rating: 3, pricePerNight: 16000, amenities: ['Budget-friendly', 'Shuttle service', '24-hour front desk'], image: ROOM3 },
        { name: 'Al Massa Hotel Makkah', city: 'Makkah', distance: '1.2km from Haram', rating: 3, pricePerNight: 13000, amenities: ['Budget-friendly', 'Shuttle service', 'Free WiFi'], image: ROOM4 },
        { name: 'Snood Ajyad Hotel', city: 'Makkah', distance: '1km from Haram', rating: 3, pricePerNight: 14500, amenities: ['24-hour front desk', 'Shuttle service', 'Simple comfortable rooms'], image: ROOM5 },

        // Madina — 5 Star
        { name: 'The Oberoi Madina', city: 'Madina', distance: '100m from Masjid Nabawi', rating: 5, pricePerNight: 68000, amenities: ['Haram view rooms', 'Award-winning dining', 'Premium spa'], image: MADINA_LANDMARK },
        { name: 'Madinah Hilton', city: 'Madina', distance: '200m from Masjid Nabawi', rating: 5, pricePerNight: 55000, amenities: ['Executive lounge', 'Direct Haram access', 'Rooftop restaurant'], image: ROOM1 },
        { name: 'Anwar Al Madinah Mövenpick', city: 'Madina', distance: '80m from Masjid Nabawi', rating: 5, pricePerNight: 32000, amenities: ['Direct Haram access', 'Breakfast included', 'Family rooms'], image: ROOM2 },
        { name: 'Crowne Plaza Madinah', city: 'Madina', distance: '300m from Masjid Nabawi', rating: 5, pricePerNight: 40000, amenities: ['Kids club', 'Family suites', 'Breakfast included'], image: ROOM3 },

        // Madina — 4 Star
        { name: 'Pullman Zamzam Madina', city: 'Madina', distance: '250m from Masjid Nabawi', rating: 4, pricePerNight: 22000, amenities: ['Free WiFi', 'Restaurant on-site', 'Shuttle to Haram'], image: ROOM4 },
        { name: 'Elaf Taiba Hotel', city: 'Madina', distance: '400m from Masjid Nabawi', rating: 4, pricePerNight: 20000, amenities: ['Free WiFi', 'Family rooms', 'Restaurant on-site'], image: ROOM5 },
        { name: 'Millennium Al Aqeeq Hotel', city: 'Madina', distance: '500m from Masjid Nabawi', rating: 4, pricePerNight: 19000, amenities: ['Shuttle to Haram', 'Breakfast included', 'Modern rooms'], image: ROOM6 },
        { name: 'Frontel Al Harithia Hotel', city: 'Madina', distance: '600m from Masjid Nabawi', rating: 4, pricePerNight: 18000, amenities: ['Free WiFi', '24-hour front desk', 'Airport transfer available'], image: ROOM7 },

        // Madina — 3 Star
        { name: 'Dallah Taibah Hotel', city: 'Madina', distance: '800m from Masjid Nabawi', rating: 3, pricePerNight: 13000, amenities: ['Budget-friendly', 'Shuttle service', 'Free WiFi'], image: ROOM1 },
        { name: 'Al Eman Royal Hotel Madinah', city: 'Madina', distance: '900m from Masjid Nabawi', rating: 3, pricePerNight: 12500, amenities: ['24-hour front desk', 'Simple comfortable rooms', 'Shuttle service'], image: ROOM2 },
        { name: 'Golden Tulip Al Noor Madinah', city: 'Madina', distance: '1km from Masjid Nabawi', rating: 3, pricePerNight: 14000, amenities: ['Free breakfast', 'Family rooms', 'Shuttle to Haram'], image: ROOM3 }
      ]
    }
  },
  async mounted() {
    this.hotels = await listItems('hotels', this.hotels)
  },
  computed: {
    filteredHotels() {
      return this.hotels.filter(hotel => {
        const cityMatch = this.selectedCity === 'All' || hotel.city === this.selectedCity
        const ratingMatch = this.selectedRating === 'All' || hotel.rating === this.selectedRating
        return cityMatch && ratingMatch
      })
    }
  },
  methods: {
    formatPrice,
    whatsappUrl(hotel) {
      const message = `I want to book *${hotel.name}* (${hotel.city}) — ${formatPrice(hotel.pricePerNight)}/night.`
      const encodedMessage = encodeURIComponent(message)
      const cleanNumber = this.whatsappNumber.replace(/[^0-9]/g, '')
      return `https://wa.me/${cleanNumber}?text=${encodedMessage}`
    }
  }
}
</script>
